import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';

import {
  FlexSalesForm,
  FlexSalesFormActions,
  SimpleSelectFormInput,
  TextFieldFormInput,
  useFlexSalesForm,
} from '../FlexSalesForm';
import {
  masterDataDataSelector,
  parametricStoresSaveSelector,
  userPermissionsDataSelector,
} from '../../state/selectors';
import { getSchema, ParametricStoreFormValues } from './schema';
import { parametricStoresCreateRequested, parametricStoresUpdateRequested } from '../../state';
import { ParametricStore } from '../../state/parametricStores/types';
import { SimpleSelectOption } from '../selects/SimpleSelect';
import { AppPermissions } from '../../types';

interface Props {
  editItem?: ParametricStore;
  onClose: () => void;
}

const StoreNameAutoFill = ({ paStores }: { paStores: SimpleSelectOption[] }) => {
  const { control, setValue } = useFlexSalesForm<ParametricStoreFormValues>();
  const storeValue = useWatch<ParametricStoreFormValues>({ control, name: 'store' });
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (storeValue) {
      const match = paStores.find(s => s.id === storeValue);
      if (match) setValue('store_name', match.description as never);
    }
  }, [storeValue, paStores, setValue]);

  return null;
};

const ParametricStoresForm = ({ editItem, onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const saveState = useSelector(parametricStoresSaveSelector);
  const masterData = useSelector(masterDataDataSelector);
  const userPermissions = useSelector(userPermissionsDataSelector);
  const resolver = useMemo(() => yupResolver(getSchema()), []);
  const submitted = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const country = useMemo(() => {
    const perm = userPermissions.find(p => p.permission === AppPermissions.PARAMETRIC_STORES);
    const countries = perm?.countries ?? [];
    if (countries.length === 1 && countries[0].countryAcronym !== 'FULL') {
      return countries[0].countryAcronym;
    }
    return 'PA';
  }, [userPermissions]);

  const storeOptions: SimpleSelectOption[] = useMemo(
    () =>
      (masterData?.stores ?? [])
        .filter(s => country && s.countryCode === country)
        .map(s => ({ id: s.storeAcronym, description: s.name })),
    [masterData, country]
  );

  const defaultValues: ParametricStoreFormValues = {
    code: editItem?.code ?? '',
    store: editItem?.store ?? '',
    store_name: editItem?.store_name ?? '',
  };

  useEffect(() => {
    if (!submitted.current) return;
    if (saveState.loaded && !saveState.failure) {
      onClose();
      return;
    }
    if (saveState.failure && saveState.error) {
      const msg = (saveState.error.data as any)?.message as string | undefined;
      setSubmitError(
        msg?.includes('already exists')
          ? t('parametricStores.form.errorCodeExists')
          : t('parametricStores.form.saveError')
      );
    }
  }, [saveState.loaded, saveState.failure, saveState.error, onClose, t]);

  const onSubmit = (values: ParametricStoreFormValues) => {
    submitted.current = true;
    setSubmitError(null);
    if (editItem) {
      dispatch(
        parametricStoresUpdateRequested(editItem.country, editItem.code, {
          store: values.store,
          store_name: values.store_name,
        })
      );
    } else {
      dispatch(
        parametricStoresCreateRequested({
          country,
          code: values.code,
          store: values.store,
          store_name: values.store_name,
        })
      );
    }
  };

  return (
    <FlexSalesForm<ParametricStoreFormValues>
      onValidSubmit={onSubmit}
      resolver={resolver}
      defaultValues={defaultValues}
    >
      <StoreNameAutoFill paStores={storeOptions} />
      <Grid container flexDirection="column" rowSpacing={2}>
        <Grid item xs>
          <TextFieldFormInput<ParametricStoreFormValues>
            name="code"
            label={t('parametricStores.form.code')}
            fullWidth
            disabled={Boolean(editItem)}
            required
          />
        </Grid>
        <Grid item xs>
          <SimpleSelectFormInput<ParametricStoreFormValues>
            name="store"
            label={t('parametricStores.form.store')}
            options={storeOptions}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs>
          <TextFieldFormInput<ParametricStoreFormValues>
            name="store_name"
            label={t('parametricStores.form.store_name')}
            fullWidth
            required
          />
        </Grid>
        {submitError && (
          <Grid item xs>
            <Alert severity="error">{submitError}</Alert>
          </Grid>
        )}
        <Grid item xs>
          <FlexSalesFormActions
            onCancel={onClose}
            cancelLabel={t('parametricStores.form.cancel')}
            submitLabel={t('parametricStores.form.save')}
          />
        </Grid>
      </Grid>
    </FlexSalesForm>
  );
};

export default ParametricStoresForm;
